<template>
  <div class="new-assignment-skills">
    <elsa-tree
      accordion
      icon-class="tree-icon"
      :data="treeData"
      :form="form"
      :filter-options="filterOptions"
      :selected-items="selectedItems"
      :filter-label="filterLabel"
      v-on:node-click="handleNodeClick"
      v-on:filter-change="handleFilterChange"
      v-on:add-remove="handleAddRemove"
    >
      <template v-slot:title="{ node }">
        <span v-bind:class="node.data.options.titleClassArray">{{ getTitleByLevel(node) }}</span>
      </template>
      <template v-slot:subtitle="{ node }">
        <span>{{ $t("homework.assignment_skill.levels", [node.data.children.length]) }}</span>
      </template>
      <template v-slot:tags="{ node }">
        <span class="tag">{{ node.data.difficulty_level }}</span>
      </template>
      <template v-slot:tag-platform-only="{ node }">
        <span class="tag platform-only">{{ node.data.options.platformAvailability }} {{ $t("common.only") }}</span>
      </template>
    </elsa-tree>
  </div>
</template>

<script>
import ElsaTree from "@/components/common/Tree";
import { propEq, find, sortBy, sortWith, ascend, descend, prop } from "ramda";
import { getGameTypeIcon } from '@/utils/gameIconFactory';

export default {
  components: {
    ElsaTree,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    lessonSelected: {
      type: Array,
      default: () => [],
    },
    filterSelected: {
      type: Array,
      default: () => [],
    },
    currentStep: {
      type: Number,
      default: 1,
    },
    filterOptionsBy: {
      type: String,
      default: "level",
    },
  },
  data() {
    return {
      defaultProps: {
        children: "children",
        label: "label",
      },
      form: {
        filterValue: undefined,
      },
      expanded: {},
      treeData: [],
      subModuleSelectInit: {},
      filterLabel: this.$t("common.choose_a_topic"),
      submoduleSelected: [],
    };
  },
  mounted() {
    this.parseData();
  },
  computed: {
    filterOptions() {
      let options = [];
      if (this.expanded.modules) {
        if (this.filterOptionsBy === "level") {
          options = this.expanded.modules.reduce((result, module) => {
            if (module.submodules.length > 0) {
              let label = `${module.topic_name} - ${module.submodules.length} ${
                module.submodules.length > 1
                  ? this.$t("homework.assignment_skill.levels_label")
                  : this.$t("homework.assignment_skill.level_label")
              }`;
              if (module.platform_availability) {
                if (module.platform_availability.toLowerCase() == "ios")
                  label = label + this.$t("homework.assignment_skill.available_ios");
                if (module.platform_availability.toLowerCase() == "android")
                  label = label + this.$t("homework.assignment_skill.available_android");
              }
              result.push({
                label: label,
                value: module.original_id,
              });
            }
            return result;
          }, []);
        } else if (this.filterOptionsBy === "skill") {
          options = this.expanded.modules.reduce((result, module) => {
            if (module.submodules.length > 0 && !result.find(e => e.value == module.original_id)) {
              let label = `${module.theme_name} - ${module.submodules.length} ${
                module.submodules.length > 1
                  ? this.$t("homework.assignment_skill.levels_label")
                  : this.$t("homework.assignment_skill.level_label")
              }`;
              if (module.platform_availability) {
                if (module.platform_availability.toLowerCase() == "ios")
                  label = label + this.$t("homework.assignment_skill.available_ios");
                if (module.platform_availability.toLowerCase() == "android")
                  label = label + this.$t("homework.assignment_skill.available_android");
              }
              result.push({
                label: label,
                value: module.original_id,
              });
            }
            return result;
          }, []);
        }
      }
      return options;
      // return sortBy(compose(toLower, prop("label")))(options);
    },
    selectedItems() {
      return {
        "level-2": this.submoduleSelected,
        "level-3": this.lessonSelected,
      };
    },
  },
  methods: {
    parseData() {
      // console.info("parseData", this.filterSelected);
      const tree = [];
      let order = 1;
      this.data.map(row => {
        const root = {
          id: row.id,
          label: row.name,
          children: [],
          original_id: row.original_id,
          order: order,
          original_order: row.order,
          options: {
            showFilter: true,
            titleClassArray: ["root-title"],
          },
        };
        for (let i = 0; i < row.modules.length; ++i) {
          const module = row.modules[i];
          if (module && module.submodules.length > 0) {
            root.children = this.appendSubmodule(module.submodules, module.platform_availability);
            if (root.children.length > 0) {
              tree.push(root);
              order++;
              this.subModuleSelectInit[row.original_id] = { original_id: module.original_id };
              // if first module have data then exit otherwise still loop to get data
              break;
            }
          }
        }
      });
      this.treeData = tree;
    },
    handleNodeClick(data, node) {
      // console.info("handleNodeClick", data, node);
      // let filterProperty = 'topic_original_id'  // filter by 'level'
      this.filterLabel = this.$t("common.choose_a_topic");
      if (this.filterOptionsBy === "skill") {
        // filterProperty = 'theme_original_id'
        this.filterLabel = this.$t("common.choose_a_skill");
      }

      if (node.level === 1 && data.original_id) {
        const theme = find(propEq("original_id", data.original_id))(this.data);
        this.expanded = theme;
        this.form.filterValue = this.subModuleSelectInit[data.original_id]["original_id"];
      }
    },
    appendSubmodule(submodules, platform_availability) {
      // console.info("appendSubmodule", submodules);
      submodules = sortBy(ascend(prop("order")))(submodules);
      const children = [];
      let order = 1;
      submodules.map(submodule => {
        const newChild = {
          id: submodule.id,
          label: submodule.name,
          children: [],
          order: order,
          original_order: submodule.order,
          original_id: submodule.original_id,
          children_original_id: [],
          options: {
            showListAction: true,
            showIcon: true,
            iconClassArray: ["elsa-level-down"],
            titleClassArray: ["level-1-title"],
            addActionText: this.$t("homework.assignment_skill.add_all_to_assignment"),
            removeActionText: this.$t("homework.assignment_skill.remove_all_from_assignment"),
            platformAvailability: platform_availability ? platform_availability.toLowerCase() : null,
          },
        };
        let lessons = sortWith([ascend(prop("order"))])(submodule.lessons);
        lessons.map(lesson => {
          const newSubChild = {
            id: lesson.id,
            label: `${lesson.name} - ${lesson.title}`,
            children: [],
            difficulty_level: lesson.difficulty_level,
            game_type: lesson.game_type,
            original_id: lesson.original_id,
            submodule_id: submodule.original_id,
            original_order: lesson.order,
            options: {
              showListAction: true,
              showTags: true,
              showIcon: true,
              iconClassArray: [getGameTypeIcon(lesson.game_type), "game-type"],
              titleClassArray: ["level-2-title"],
            },
          };
          if (
            this.filterSelected.length === 0 ||
            (this.filterSelected.length > 0 && this.filterSelected.includes(lesson.difficulty_level))
          ) {
            newChild.children.push(newSubChild);
            newChild.children_original_id.push(lesson.original_id);
          }
        });
        if (newChild.children.length > 0) {
          children.push(newChild);
          order++;
        }
      });
      return children;
    },
    handleFilterChange(node, data) {
      // let filterProperty = 'topic_original_id'  // filter by 'level'

      // if (this.filterOptionsBy === 'skill')
      //     filterProperty = 'theme_original_id'

      const module = find(propEq("original_id", this.form.filterValue))(this.expanded.modules);
      data.children = this.appendSubmodule(module.submodules, module.platform_availability);
    },
  
    async addRemoveLesson(lesson, parent) {
      // console.info('addRemoveLesson', parent.original_id, lesson.original_id)
      if (!this.lessonSelected.includes(lesson.original_id)) {
        await this.$emit("add-lesson", [lesson.original_id]);
        let is_subset = !parent.children_original_id.some(val => this.lessonSelected.indexOf(val) === -1);
        if (is_subset) this.submoduleSelected.push(parent.original_id);
      } else {
        await this.$emit("remove-lesson", [lesson.original_id]);
        let is_subset = !parent.children_original_id.some(val => this.lessonSelected.indexOf(val) === -1);
        if (!is_subset) {
          let index = this.submoduleSelected.indexOf(parent.original_id);
          if (index > -1) this.submoduleSelected.splice(index, 1);
        }
      }
    },
    addRemoveSubmodule(submodule) {
      if (!this.submoduleSelected.includes(submodule.original_id)) {
        this.$emit("add-lesson", submodule.children_original_id);
        // this.$emit("add-submodule", submodule.original_id);
        this.submoduleSelected.push(submodule.original_id);
      } else {
        this.$emit("remove-lesson", submodule.children_original_id);
        // this.$emit("remove-submodule", submodule.original_id);
        let index = this.submoduleSelected.indexOf(submodule.original_id);
        if (index > -1) this.submoduleSelected.splice(index, 1);
      }
    },
    handleAddRemove(node) {
      if (node.level === 2) {
        this.addRemoveSubmodule(node.data);
      } else if (node.level === 3) {
        this.addRemoveLesson(node.data, node.parent.data);
      }
    },
    getTitleByLevel(node) {
      let prefix = "";

      if (node.level === 1) {
        prefix = this.$t("homework.assignment_skill.skill_title", [node.data.order]);
        if (this.filterOptionsBy === "skill") {
          prefix = this.$t("homework.assignment_skill.topic_title", [node.data.order]);
        }
      }

      if (node.level === 2) prefix = this.$t("homework.assignment_skill.level_title", [node.data.order]);
      return prefix + node.label;
    },
    setLesson() {
      // this.updateBuildingClass(this.goals);
      this.$emit("next-step", this.currentStep);
    },
  },
  watch: {
    filterSelected(val, oldVal) {
      // console.info("new: %s, old: %s", val, oldVal);
      if (val !== oldVal) {
        this.parseData();
      }
    },
    data: {
      handler() {
        this.parseData();
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.tag {
  padding: 4px 8px;
  background: #efeff4;
  border-radius: 4px;
  text-transform: uppercase;
  margin-top: 4px;
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 0.25px;
  // font-variant: small-caps;
}
.platform-only {
  background: rgba(0, 126, 255, 0.1) !important;
  font-family: SF Pro Display, sans-serif;
  font-style: normal;
  font-weight: 700;
  color: #007eff;
  margin-top: 0px;
}
.root-title {
  font-weight: 600;
  font-size: 20px;
  line-height: 20px;
  /*text-transform: capitalize;*/
  letter-spacing: 0.25px;
  color: #121131;
  font-family: sf-pro-text, sans-serif;
}

.level-1-title {
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #121131;
}

.level-2-title {
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0.25px;
  color: #121131;
  font-weight: 400;
}
</style>
